/**
 * Text input primitive. Controlled (`value` + `onValue`), token-driven, with an
 * optional visible label, invalid state (`aria-invalid`), and focus-visible
 * ring. Presentation only — validation lives in the caller.
 */
import type { JSX } from 'solid-js';
import { Show, splitProps } from 'solid-js';
import { ensureBaseStyles } from '../internal/base-styles.ts';
import { cx } from '../internal/cx.ts';
import { ensureStyles } from '../internal/inject-styles.ts';

const STYLE_ID = 'f4-textinput';
const CSS = `
.f4-field { display: flex; flex-direction: column; gap: var(--space-1); }
.f4-field__label { font-size: var(--font-size-sm); font-weight: var(--font-weight-semibold); color: var(--color-text); }
.f4-input {
  width: 100%;
  padding: var(--space-2) var(--space-3);
  font: inherit;
  color: var(--color-text);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  transition: border-color var(--duration-fast, 150ms) var(--ease-standard, ease);
}
.f4-input::placeholder { color: var(--color-text-subtle); }
.f4-input { --f4-focus-offset: 1px; }
.f4-input:focus-visible { border-color: var(--color-accent); }
.f4-input[aria-invalid="true"] { border-color: var(--color-danger); }
.f4-input[disabled] { opacity: 0.6; cursor: not-allowed; background: var(--color-surface-sunken); }
@media (prefers-reduced-motion: reduce) { .f4-input { transition: none; } }
`;

export interface TextInputProps
  extends Omit<JSX.InputHTMLAttributes<HTMLInputElement>, 'onInput' | 'value' | 'type'> {
  /** Current value. */
  readonly value: string;
  /** Emitted with the new value on every input. */
  readonly onValue: (value: string) => void;
  /** Input type. Defaults to `text`. */
  readonly type?: 'text' | 'email' | 'password' | 'search' | 'tel' | 'url';
  /** Visible label text. Rendered as a `<label>` when provided. */
  readonly label?: string;
  /** Element id — links the label and is required if `label` is set. */
  readonly id?: string;
  /** Mark the field invalid (`aria-invalid`). */
  readonly invalid?: boolean;
  readonly class?: string;
}

export function TextInput(props: TextInputProps): JSX.Element {
  ensureBaseStyles();
  ensureStyles(STYLE_ID, CSS);
  const [local, rest] = splitProps(props, [
    'value',
    'onValue',
    'type',
    'label',
    'id',
    'invalid',
    'class',
  ]);

  return (
    <div class="f4-field">
      <Show when={local.label}>
        <label class="f4-field__label" for={local.id}>
          {local.label}
        </label>
      </Show>
      <input
        {...rest}
        id={local.id}
        type={local.type ?? 'text'}
        class={cx('f4-input', 'f4-focus-ring', local.class)}
        value={local.value}
        aria-invalid={local.invalid ? 'true' : undefined}
        onInput={(event) => local.onValue(event.currentTarget.value)}
      />
    </div>
  );
}
